// Form rules for the Leads screens, the same as the API's input rules
// (apps/api/src/modules/leads/schemas.ts): the form shows each problem next to its field before
// anything is sent. The API stays the authority and checks everything again.
import { z } from 'zod';
import type { LeadsKind } from './leads.ts';

const LONG_TEXT = 10_000;
const PACK_LABEL = 500;
const TITLE = 300;
const LABEL = 64;
const REASON = 500;
const HINT = 200;
export const LEADS_NOTE_MAX = 2000;
export const READING_NOTE_MAX = REASON;
export const READING_VALUE_MAX = HINT;
const MAX_LINES = 50;

// Task id in an ask: matches "T-OPS-026"; rejects "T-OPS-26", "t-ops-026".
// https://regex101.com/?flavor=javascript&regex=%5ET-%5BA-Z%5D%7B3%7D-%5Cd%7B3%7D%24&testString=T-OPS-026%0AT-OPS-26%0At-ops-026
const TASK_ID = /^T-[A-Z]{3}-\d{3}$/;
// Calendar date: matches "2026-11-04"; rejects "4 Nov", "2026-11-4".
// https://regex101.com/?flavor=javascript&regex=%5E%5Cd%7B4%7D-%5Cd%7B2%7D-%5Cd%7B2%7D%24&testString=2026-11-04%0A2026-11-4
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** How a field is typed: one line, a paragraph, or a list with one entry per line. */
export type LeadsFieldInput = 'line' | 'text' | 'list';

export interface LeadsEditField {
  readonly name: string;
  readonly label: string;
  readonly input: LeadsFieldInput;
  readonly max: number;
  readonly required: boolean;
  /** Shown under the field. */
  readonly hint?: string;
}

const line = (name: string, label: string, max: number, required = false): LeadsEditField => ({
  name,
  label,
  input: 'line',
  max,
  required,
});
const para = (name: string, label: string, required = false): LeadsEditField => ({
  name,
  label,
  input: 'text',
  max: LONG_TEXT,
  required,
});

const DATE_HINT = 'A date like 2027-01-15, or words such as "after the readout".';

/** Every field the owner may change, per record type, in form order (spec §5.7). */
export const LEADS_EDIT_FIELDS: Readonly<Record<LeadsKind, readonly LeadsEditField[]>> = {
  risks: [
    para('risk', 'Risk', true),
    line('likelihood', 'Likelihood', PACK_LABEL),
    line('impact', 'Impact', PACK_LABEL),
    para('effect', 'Effect'),
    para('mitigation', 'Mitigation'),
    line('owner', 'Owner', PACK_LABEL),
    line('status', 'Status', PACK_LABEL, true),
  ],
  acceptances: [
    para('risk', 'Risk accepted', true),
    para('cis', 'CIS safeguards'),
    para('compensatingControl', 'Compensating control'),
    line('accepter', 'Accepted by', PACK_LABEL),
    { ...line('reviewOnText', 'Review on', PACK_LABEL), hint: DATE_HINT },
  ],
  asks: [
    para('decisionAsked', 'Decision asked', true),
    { ...line('deadlineText', 'Deadline', PACK_LABEL), hint: DATE_HINT },
    line('approver', 'Approver', PACK_LABEL),
    line('cost', 'Cost', REASON),
    para('why', 'Why'),
    {
      name: 'tasks',
      label: 'Tasks',
      input: 'list',
      max: MAX_LINES,
      required: false,
      hint: 'One task id per line, like T-OPS-026.',
    },
  ],
  decisions: [
    line('topic', 'Topic', PACK_LABEL, true),
    para('decision', 'Decision', true),
    line('by', 'Decided by', PACK_LABEL),
  ],
  kpis: [
    para('kpi', 'KPI', true),
    para('baseline', 'Baseline'),
    para('target', 'Target'),
    line('dataSource', 'Where the reading comes from', PACK_LABEL),
    line('cadence', 'Cadence', PACK_LABEL),
  ],
  gates: [
    line('title', 'Title', TITLE, true),
    {
      name: 'criteria',
      label: 'Criteria',
      input: 'list',
      max: MAX_LINES,
      required: true,
      hint: 'One criterion per line.',
    },
    line('status', 'Status', LABEL, true),
  ],
  questions: [
    para('text', 'Question', true),
    line('owner', 'Owner', PACK_LABEL),
    line('status', 'Status', PACK_LABEL, true),
  ],
};

export type LeadsEditValues = Record<string, string>;

/** The lines of a list field, trimmed, without blank lines. */
export function listLines(value: string): string[] {
  return value
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');
}

function listRule(kind: LeadsKind, field: LeadsEditField): z.ZodType<string> {
  const each = kind === 'asks' ? 'Use a task id like T-OPS-026 on each line' : null;
  return z.string().superRefine((value, ctx) => {
    const lines = listLines(value);
    if (field.required && lines.length === 0) ctx.addIssue({ code: 'custom', message: 'Required' });
    if (lines.length > field.max) {
      ctx.addIssue({ code: 'custom', message: `At most ${String(field.max)} lines` });
    }
    if (each !== null && lines.some((l) => !TASK_ID.test(l))) {
      ctx.addIssue({ code: 'custom', message: each });
    }
    if (each === null && lines.some((l) => l.length > LONG_TEXT)) {
      ctx.addIssue({
        code: 'custom',
        message: `Each line at most ${String(LONG_TEXT)} characters`,
      });
    }
  });
}

function fieldRule(kind: LeadsKind, field: LeadsEditField): z.ZodType<string> {
  if (field.input === 'list') return listRule(kind, field);
  const capped = z
    .string()
    .trim()
    .max(field.max, `At most ${String(field.max)} characters`);
  return field.required ? capped.min(1, 'Required') : capped;
}

/**
 * The form schema for one record type's edit form. A field left as it was (`original`) always
 * passes: only changed fields are sent, so a long pack value the owner did not touch never
 * blocks saving another field.
 */
export function leadsEditSchema(
  kind: LeadsKind,
  original: Readonly<LeadsEditValues> = {},
): z.ZodType<LeadsEditValues, LeadsEditValues> {
  const shape = Object.fromEntries(
    LEADS_EDIT_FIELDS[kind].map((f) => {
      const rule = fieldRule(kind, f);
      const checked = z.string().superRefine((value, ctx) => {
        if (value === original[f.name]) return;
        const result = rule.safeParse(value);
        if (!result.success) {
          result.error.issues.forEach((i) => ctx.addIssue({ code: 'custom', message: i.message }));
        }
      });
      return [f.name, checked];
    }),
  );
  return z.object(shape) as unknown as z.ZodType<LeadsEditValues, LeadsEditValues>;
}

/** The form's starting values from the record as shown (null becomes empty, lists one per line). */
export function leadsEditValues(
  kind: LeadsKind,
  record: Readonly<Record<string, unknown>>,
): LeadsEditValues {
  return Object.fromEntries(
    LEADS_EDIT_FIELDS[kind].map((f) => {
      const value = record[f.name];
      if (Array.isArray(value)) return [f.name, value.map(String).join('\n')];
      return [f.name, typeof value === 'string' ? value : ''];
    }),
  );
}

function apiValue(field: LeadsEditField, value: string): unknown {
  if (field.input === 'list') return listLines(value);
  const trimmed = value.trim();
  return trimmed === '' && !field.required ? null : trimmed;
}

/** Only the fields the person changed, as the PATCH body wants them; {} when nothing changed. */
export function leadsEditPatch(
  kind: LeadsKind,
  record: Readonly<Record<string, unknown>>,
  values: LeadsEditValues,
): Record<string, unknown> {
  const before = leadsEditValues(kind, record);
  const changed = LEADS_EDIT_FIELDS[kind].filter((f) => {
    const now = apiValue(f, values[f.name] ?? '');
    return JSON.stringify(now) !== JSON.stringify(apiValue(f, before[f.name] ?? ''));
  });
  return Object.fromEntries(changed.map((f) => [f.name, apiValue(f, values[f.name] ?? '')]));
}

// A decision on an ask or an acceptance (spec J4): a note is required for Declined (qa Q30).
export const leadsDecisionFormSchema = z
  .object({
    decision: z.string().min(1, 'Choose a decision'),
    note: z
      .string()
      .trim()
      .max(LEADS_NOTE_MAX, `At most ${String(LEADS_NOTE_MAX)} characters`),
  })
  .refine((v) => v.decision !== 'declined' || v.note !== '', {
    message: 'Say why it was declined',
    path: ['note'],
  });
export type LeadsDecisionForm = z.infer<typeof leadsDecisionFormSchema>;

/** A KPI reading; `today` is the program's calendar date, since a reading cannot be in the future. */
export function kpiReadingFormSchema(today: string) {
  return z.object({
    readOn: z
      .string()
      .regex(ISO_DAY, 'Use a calendar date')
      .refine((d) => d <= today, 'A reading cannot be dated in the future'),
    value: z
      .string()
      .trim()
      .min(1, 'Required')
      .max(READING_VALUE_MAX, `At most ${String(READING_VALUE_MAX)} characters`),
    note: z
      .string()
      .trim()
      .max(READING_NOTE_MAX, `At most ${String(READING_NOTE_MAX)} characters`),
  });
}
export type KpiReadingForm = z.infer<ReturnType<typeof kpiReadingFormSchema>>;
